/** Định dạng & đọc số theo lối Việt Nam (nghìn «.», thập phân «,»). Hàm thuần, không React. */

const integerFormat = new Intl.NumberFormat('vi-VN');
const twoDecimalFormat = new Intl.NumberFormat('vi-VN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Ô chưa có số hiện dấu chấm giữa — KHÔNG hiện 0, vì «0» là một con số thật. */
export const EMPTY_CELL = '·';

type Nullable = number | null | undefined;
const isMissing = (value: Nullable): value is null | undefined =>
  value === null || value === undefined || Number.isNaN(value);

export function formatNumber(value: Nullable): string {
  return isMissing(value) ? EMPTY_CELL : integerFormat.format(value);
}

/** ≥ 1000 thì rút gọn thành «1,2 N» (N = nghìn). */
export function formatCompact(value: Nullable): string {
  if (isMissing(value)) return EMPTY_CELL;
  if (Math.abs(value) < 1000) return integerFormat.format(value);
  return `${integerFormat.format(Number((value / 1000).toFixed(1)))} N`;
}

export function formatPercent(value: Nullable): string {
  return isMissing(value) ? EMPTY_CELL : `${twoDecimalFormat.format(value)}%`;
}

/** Tiền USD, 2 chữ số lẻ. Luôn đi qua hàm này: định dạng thẳng `null` sẽ in ra «0,00». */
export function formatMoney(value: Nullable): string {
  return isMissing(value) ? EMPTY_CELL : twoDecimalFormat.format(value);
}

/** Số → chuỗi để đổ sẵn vào ô nhập («5.7» → «5,7»). */
export function toInputString(value: Nullable): string {
  return isMissing(value) ? '' : String(value).replace('.', ',');
}

/** Giây → «m:ss» (phút không giới hạn). */
export function formatMinutes(seconds: Nullable): string {
  if (isMissing(seconds)) return EMPTY_CELL;
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Giây → «h:mm:ss» nếu từ 1 giờ, ngược lại «m:ss». */
export function formatDuration(seconds: Nullable): string {
  if (isMissing(seconds)) return EMPTY_CELL;
  const total = Math.max(0, Math.round(seconds));
  if (total < 3600) return formatMinutes(total);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * ĐỌC thời lượng đã lưu: «12:35», «1:12:35» hoặc toàn chữ số (= giây) → số giây.
 *
 * Đọc DỄ DÃI, đúng như hệ thống cũ («9:5», «12:75» vẫn ra số): dữ liệu đang lưu có những giá trị
 * như thế, và các con số tổng/trung bình người dùng vẫn thấy hằng ngày đã tính cả chúng. Siết ở
 * đây là một video lặng lẽ rơi khỏi mọi phép tính (đối chiếu 21/09/2026: AVD trung bình tháng 9
 * lệch 1 giây chỉ vì MỘT video như vậy). Muốn KIỂM người dùng gõ có chuẩn không thì dùng
 * `isWellFormedDuration` ở form nhập.
 */
export function parseDuration(raw: string | null | undefined): number | null {
  const text = (raw ?? '').trim();
  if (!text) return null;
  if (/^\d+$/.test(text)) return Number(text);
  const parts = text.split(':').map((part) => part.trim());
  if (parts.length < 2 || parts.length > 3) return null;
  const numbers = parts.map(Number);
  if (parts.some((part) => part === '') || numbers.some((n) => Number.isNaN(n) || n < 0))
    return null;
  const [first = 0, second = 0, third = 0] = numbers;
  return parts.length === 2 ? first * 60 + second : first * 3600 + second * 60 + third;
}

/** KIỂM chặt cho form nhập: «m:ss» / «h:mm:ss» (giây, phút ≤ 59) hoặc số giây nguyên. */
export function isWellFormedDuration(raw: string): boolean {
  const text = raw.trim();
  if (/^\d+$/.test(text)) return true;
  const match = /^(?:(\d+):)?(\d{1,3}):(\d{2})$/.exec(text);
  if (!match) return false;
  const [, hours, minutes, seconds] = match;
  return Number(seconds) <= 59 && (hours === undefined || Number(minutes) <= 59);
}

export function median(values: readonly number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const upper = sorted[middle] as number;
  return sorted.length % 2 ? upper : ((sorted[middle - 1] as number) + upper) / 2;
}

/**
 * Đọc số người dùng gõ / dán từ YouTube Studio: «1,1 N» → 1100, «25,3N» → 25300,
 * «2 tr» → 2.000.000, «5,70%» → 5.7.
 *
 * Dấu chấm là chỗ NHẬP NHẰNG: xoá bừa mọi dấu chấm từng biến RPM «0.5» thành «5» — sai 10 lần
 * mà không ai hay. Quy tắc:
 *   • có cả «,» lẫn «.» → dấu đứng SAU CÙNG là dấu thập phân;
 *   • chỉ có «,»        → thập phân;
 *   • chỉ có «.»        → là dấu nghìn CHỈ KHI khớp dạng 1.100 / 12.345.678; còn lại là thập phân
 *                         («0.5» = 0,5 · «12.34» = 12,34).
 */
export function parseVietnameseNumber(raw: string | null | undefined): number | null {
  let text = (raw ?? '').trim().replace(/%$/, '').trim();
  if (!text) return null;

  let multiplier = 1;
  const suffix = /\s*(n|tr)$/i.exec(text);
  if (suffix) {
    multiplier = suffix[1]?.toLowerCase() === 'tr' ? 1_000_000 : 1_000;
    text = text.slice(0, suffix.index);
  }
  text = text.replace(/\s+/g, '').replace(/[.,]$/, ''); // «12,» = đang gõ dở

  let sign = 1;
  if (/^[+-]/.test(text)) {
    if (text.startsWith('-')) sign = -1;
    text = text.slice(1);
  }

  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  if (lastComma >= 0 && lastDot >= 0) {
    const decimal = lastComma > lastDot ? ',' : '.';
    const thousands = decimal === ',' ? '.' : ',';
    text = text.split(thousands).join('').replace(decimal, '.');
  } else if (lastComma >= 0) {
    text = text.replace(',', '.');
  } else if (lastDot >= 0 && /^\d{1,3}(\.\d{3})+$/.test(text)) {
    text = text.split('.').join('');
  }

  if (!/^\d*\.?\d+$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? sign * value * multiplier : null;
}

/**
 * Chuẩn hoá để SO TRÙNG tên: NFC, chữ thường, bỏ dấu nháy, ký tự không phải chữ/số → khoảng
 * trắng. GIỮ dấu tiếng Việt — «Nhạc Vàng» và «Nhac Vang» là hai tên khác nhau.
 */
export function normalizeForComparison(text: string | null | undefined): string {
  return (text ?? '')
    .normalize('NFC')
    .toLowerCase()
    .replace(/['’`´]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * Chuẩn hoá để TÌM KIẾM: như trên nhưng BỎ DẤU tiếng Việt, để gõ «nhac vang» hay «tran binh» vẫn ra
 * «Nhạc Vàng», «Trần Bình» — gõ không dấu là thói quen khi tìm nhanh. Áp cho CẢ chữ cần tìm lẫn chữ
 * được tìm. Chỉ dùng cho ô tìm; so trùng tên thì dùng `normalizeForComparison` (giữ dấu).
 */
export function normalizeForSearch(text: string | null | undefined): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/đ/g, 'd')
    .replace(/['’`´]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}
